import {
  IOpeningHoursDay,
  IOpeningHoursException,
} from "../../store/useDropLocations";

// Dashboard is in English; day 0 = Monday ... 6 = Sunday to match the backend.
const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

interface Props {
  value?: IOpeningHoursDay[];
  onChange: (next: IOpeningHoursDay[]) => void;
  label?: string;
  /** Dated one-off overrides (holidays). Kept separate from the weekday rows
   *  because a holiday falls on a different weekday each year. */
  exceptions?: IOpeningHoursException[];
  onExceptionsChange?: (next: IOpeningHoursException[]) => void;
  /** Free-text line under the week, for anything the structure can't express. */
  note?: { en?: string; nl?: string };
  onNoteChange?: (next: { en?: string; nl?: string }) => void;
}

const today = () => new Date().toISOString().slice(0, 10);

/** Always work with a full, ordered Mon-Sun array so the editor is stable even
 *  when the stored value is partial or empty (older locations). */
const normalize = (value?: IOpeningHoursDay[]): IOpeningHoursDay[] =>
  DAYS.map((_, day) => {
    const existing = value?.find((d) => d.day === day);
    return {
      day,
      closed: existing?.closed ?? false,
      open: existing?.open ?? "",
      close: existing?.close ?? "",
      note: {
        en: existing?.note?.en ?? "",
        nl: existing?.note?.nl ?? "",
      },
    };
  });

export const OpeningHoursEditor = ({
  value,
  onChange,
  label,
  exceptions,
  onExceptionsChange,
  note,
  onNoteChange,
}: Props) => {
  const days = normalize(value);

  const patch = (day: number, changes: Partial<IOpeningHoursDay>) =>
    onChange(days.map((d) => (d.day === day ? { ...d, ...changes } : d)));

  const exceptionList = exceptions ?? [];

  const patchException = (
    index: number,
    changes: Partial<IOpeningHoursException>
  ) =>
    onExceptionsChange?.(
      exceptionList.map((item, i) =>
        i === index ? { ...item, ...changes } : item
      )
    );

  const addException = () =>
    onExceptionsChange?.([
      ...exceptionList,
      {
        startDate: today(),
        endDate: today(),
        closed: true,
        open: "",
        close: "",
        note: { en: "", nl: "" },
      },
    ]);

  // Keep the range coherent: moving the start past the end drags the end along,
  // so an impossible range can't be typed in the first place.
  const patchStartDate = (index: number, startDate: string) => {
    const current = exceptionList[index];
    const endDate =
      current?.endDate && current.endDate >= startDate
        ? current.endDate
        : startDate;
    patchException(index, { startDate, endDate });
  };

  const removeException = (index: number) =>
    onExceptionsChange?.(exceptionList.filter((_, i) => i !== index));

  return (
    <div className="mb-4">
      {label ? (
        <p className="font-satoshi text-dark text-sm font-medium mb-3">
          {label}
        </p>
      ) : null}
      <div className="flex flex-col gap-2">
        {days.map((d) => (
          <div
            key={d.day}
            className="flex flex-wrap items-center gap-2 bg-sky-lighter border border-gray-border rounded px-3 py-2"
          >
            <span className="font-satoshi text-dark text-sm font-medium w-24 shrink-0">
              {DAYS[d.day]}
            </span>

            <label className="flex items-center gap-1 font-satoshi text-dark text-sm w-24 shrink-0">
              <input
                type="checkbox"
                checked={d.closed ?? false}
                onChange={(e) => patch(d.day, { closed: e.target.checked })}
              />
              Closed
            </label>

            {/* One unit, so the pair never wraps apart when the row runs out
                of width. */}
            <div className="flex items-center gap-2 shrink-0">
              <input
                type="time"
                disabled={d.closed}
                value={d.open ?? ""}
                onChange={(e) => patch(d.day, { open: e.target.value })}
                className="h-[38px] px-2 bg-white border border-gray-border rounded font-satoshi text-sm text-dark outline-none disabled:opacity-40"
              />
              <span className="text-dark">-</span>
              <input
                type="time"
                disabled={d.closed}
                value={d.close ?? ""}
                onChange={(e) => patch(d.day, { close: e.target.value })}
                className="h-[38px] px-2 bg-white border border-gray-border rounded font-satoshi text-sm text-dark outline-none disabled:opacity-40"
              />
            </div>

            <input
              type="text"
              value={d.note?.en ?? ""}
              onChange={(e) =>
                patch(d.day, { note: { ...d.note, en: e.target.value } })
              }
              placeholder="Note EN (e.g. holiday) - optional"
              className="flex-1 min-w-[160px] h-[38px] px-3 bg-white border border-gray-border rounded font-satoshi text-sm text-dark outline-none"
            />
            <input
              type="text"
              value={d.note?.nl ?? ""}
              onChange={(e) =>
                patch(d.day, { note: { ...d.note, nl: e.target.value } })
              }
              placeholder="Note NL (bijv. feestdag) - optioneel"
              className="flex-1 min-w-[160px] h-[38px] px-3 bg-white border border-gray-border rounded font-satoshi text-sm text-dark outline-none"
            />
          </div>
        ))}
      </div>

      {onExceptionsChange ? (
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <p className="font-satoshi text-dark text-sm font-medium">
              Exceptions (holidays)
            </p>
            <button
              type="button"
              onClick={addException}
              className="font-satoshi text-xs font-medium px-3 py-1 rounded border border-gray-border text-dark"
            >
              + Add date
            </button>
          </div>
          <p className="font-satoshi text-xs text-gray-500 mb-2">
            Overrides the weekdays above for a date, or a stretch of them - pick
            the same day twice for a single date. Either tick Closed, or leave it
            off and set the hours the location keeps that day; a row with neither
            says nothing and won't be saved. Dates in the past disappear on their
            own, so there's nothing to clean up next year.
          </p>

          {exceptionList.length ? (
            <div className="flex flex-col gap-2">
              {exceptionList.map((exception, index) => {
                // Not closed and no hours = an empty statement; flag it here so
                // it's obvious before saving drops it.
                const isIncomplete =
                  !exception.closed && !(exception.open && exception.close);
                return (
                <div
                  key={index}
                  className={`flex flex-wrap items-center gap-2 bg-sky-lighter border rounded px-3 py-2 ${
                    isIncomplete ? "border-red-base" : "border-gray-border"
                  }`}
                >
                  {/* Dates sit where the weekday name does on the rows above,
                      and stay together as one unit. */}
                  <div className="flex items-center gap-2 shrink-0">
                    <input
                      type="date"
                      value={exception.startDate ?? ""}
                      onChange={(e) => patchStartDate(index, e.target.value)}
                      className="h-[38px] px-2 bg-white border border-gray-border rounded font-satoshi text-sm text-dark outline-none"
                    />
                    <span className="text-dark">-</span>
                    <input
                      type="date"
                      // Same day = a one-off; a later day closes a whole stretch.
                      min={exception.startDate ?? undefined}
                      value={exception.endDate ?? ""}
                      onChange={(e) =>
                        patchException(index, { endDate: e.target.value })
                      }
                      className="h-[38px] px-2 bg-white border border-gray-border rounded font-satoshi text-sm text-dark outline-none"
                    />
                  </div>

                  <label className="flex items-center gap-1 font-satoshi text-dark text-sm w-24 shrink-0">
                    <input
                      type="checkbox"
                      checked={exception.closed ?? false}
                      onChange={(e) =>
                        patchException(index, { closed: e.target.checked })
                      }
                    />
                    Closed
                  </label>

                  <div className="flex items-center gap-2 shrink-0">
                    <input
                      type="time"
                      disabled={exception.closed}
                      value={exception.open ?? ""}
                      onChange={(e) =>
                        patchException(index, { open: e.target.value })
                      }
                      className="h-[38px] px-2 bg-white border border-gray-border rounded font-satoshi text-sm text-dark outline-none disabled:opacity-40"
                    />
                    <span className="text-dark">-</span>
                    <input
                      type="time"
                      disabled={exception.closed}
                      value={exception.close ?? ""}
                      onChange={(e) =>
                        patchException(index, { close: e.target.value })
                      }
                      className="h-[38px] px-2 bg-white border border-gray-border rounded font-satoshi text-sm text-dark outline-none disabled:opacity-40"
                    />
                  </div>

                  <input
                    type="text"
                    value={exception.note?.en ?? ""}
                    onChange={(e) =>
                      patchException(index, {
                        note: { ...exception.note, en: e.target.value },
                      })
                    }
                    placeholder="Reason EN - optional"
                    className="flex-1 min-w-[160px] h-[38px] px-3 bg-white border border-gray-border rounded font-satoshi text-sm text-dark outline-none"
                  />
                  <input
                    type="text"
                    value={exception.note?.nl ?? ""}
                    onChange={(e) =>
                      patchException(index, {
                        note: { ...exception.note, nl: e.target.value },
                      })
                    }
                    placeholder="Reden NL - optioneel"
                    className="flex-1 min-w-[160px] h-[38px] px-3 bg-white border border-gray-border rounded font-satoshi text-sm text-dark outline-none"
                  />

                  <button
                    type="button"
                    onClick={() => removeException(index)}
                    className="font-satoshi text-xs font-medium px-2 py-1 rounded border border-red-base text-red-base"
                  >
                    Remove
                  </button>
                </div>
                );
              })}
            </div>
          ) : (
            <p className="font-satoshi text-sm text-gray-500">
              No exceptions yet.
            </p>
          )}
        </div>
      ) : null}

      {onNoteChange ? (
        <div className="mt-5">
          <p className="font-satoshi text-dark text-sm font-medium mb-2">
            General note
          </p>
          <p className="font-satoshi text-xs text-gray-500 mb-2">
            Shown under the opening hours in the app. Use it for anything the
            week and the dates above can't say - it stays until you remove it.
          </p>
          <div className="flex flex-wrap gap-2">
            <input
              type="text"
              value={note?.en ?? ""}
              onChange={(e) => onNoteChange({ ...note, en: e.target.value })}
              placeholder="Note EN - optional"
              className="flex-1 min-w-[220px] h-[38px] px-3 bg-white border border-gray-border rounded font-satoshi text-sm text-dark outline-none"
            />
            <input
              type="text"
              value={note?.nl ?? ""}
              onChange={(e) => onNoteChange({ ...note, nl: e.target.value })}
              placeholder="Notitie NL - optioneel"
              className="flex-1 min-w-[220px] h-[38px] px-3 bg-white border border-gray-border rounded font-satoshi text-sm text-dark outline-none"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};
